import { useEffect, useState, type FormEvent } from 'react';
import { READING_LOCALES, UI_LOCALES } from '@tc/domain';
import { Brand, BRAND } from './Brand.tsx';
import { api, ApiError, NetworkError } from '../api.ts';
import type { MeDTO, PrivacyInfoDTO } from '@tc/contracts';
import { acceptNotice, bootstrap, login, loginWithCode, logout, setMe, setNativeLocale, useStore } from '../store.ts';
import { cancelled, passkeysSupported, signInWithPasskey } from '../passkeys.ts';
import { LanguageStep, LanguageSwitch, NATIVE_NAMES, uiFor } from './LanguageStep.tsx';
import { ConsentFields, PrivacyNoticeText } from './PrivacyNotice.tsx';
import { browserUiLocale, languageName, seal, translate, useT, useUiLocale, type MsgKey } from '../i18n.ts';
import { clearHidden, faceIdOn, signInWithFaceId } from '../faceid.ts';
import { isNative, nativePlatform } from '../native.ts';

const nativeAndroid = isNative && nativePlatform === 'android';

function errorKey(err: unknown): MsgKey {
  if (err instanceof NetworkError) return 'networkError';
  if (err instanceof ApiError) {
    if (err.code === 'invalid_credentials') return 'invalidCredentials';
    if (err.code === 'account_locked') return 'accountLocked';
    if (err.code === 'invite_invalid') return 'inviteInvalid';
    if (err.code === 'reset_invalid') return 'resetInvalid';
    if (err.code === 'passkey_invalid') return 'passkeyInvalid';
    if (err.code === 'mfa_code_invalid') return 'mfaCodeInvalid';
    if (err.code === 'mfa_ticket_invalid') return 'mfaTicketInvalid';
  }
  return 'networkError';
}

function AuthFrame({ title, children, top }: { title: string; children: React.ReactNode; top?: React.ReactNode }) {
  return (
    <main className="auth">
      <div className="auth-card">
        {top}
        {title === BRAND ? <Brand as="h1" /> : (
          <>
            <Brand />
            <h1>{title}</h1>
          </>
        )}
        {children}
      </div>
    </main>
  );
}

export function LoginScreen() {
  const t = useT();
  const ui = useUiLocale();
  const [mode, setMode] = useState<'login' | 'forgot' | 'code'>('login');
  const [ticket, setTicket] = useState('');
  const [code, setCode] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<MsgKey | null>(null);
  const [sent, setSent] = useState<'email' | 'admin' | null>(null);
  // iPhone app with Face ID sign-in turned on: ask for Face ID as soon as the app opens (not right
  // after the person signed out on purpose).
  const [faceId, setFaceId] = useState(false);
  const faceIdLogin = async () => {
    setBusy(true);
    setError(null);
    try {
      await signInWithFaceId();
      await bootstrap();
    } catch (err) {
      const why = (err as Error).message;
      if (why === 'revoked') {
        setFaceId(false);
        setError('faceIdRevoked');
      } else if (why !== 'cancelled') setError(errorKey(err));
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    let alive = true;
    void faceIdOn().then((on) => {
      if (!alive || !on) return;
      setFaceId(true);
      let justSignedOut = false;
      try {
        justSignedOut = sessionStorage.getItem('tc.signedOut') === '1';
        sessionStorage.removeItem('tc.signedOut');
      } catch {
        /* ignore */
      }
      if (!justSignedOut) void faceIdLogin();
    });
    return () => {
      alive = false;
      // Leaving the sign-in screen means signed in: the app lock starts counting from now.
      clearHidden();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await login(email, password);
      if (r) {
        setTicket(r.mfaTicket);
        setCode('');
        setMode('code');
      }
    } catch (err) {
      setError(errorKey(err));
    } finally {
      setBusy(false);
    }
  };
  const submitCode = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await loginWithCode(ticket, code);
    } catch (err) {
      const key = errorKey(err);
      setError(key);
      if (key === 'mfaTicketInvalid') {
        setMode('login');
        setPassword('');
      }
    } finally {
      setBusy(false);
    }
  };
  const passkey = async () => {
    setBusy(true);
    setError(null);
    try {
      await signInWithPasskey();
      await bootstrap();
    } catch (err) {
      if (!cancelled(err)) setError(err instanceof ApiError || err instanceof NetworkError ? errorKey(err) : 'passkeyInvalid');
    } finally {
      setBusy(false);
    }
  };
  const forgot = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ via: 'email' | 'admin' }>('POST', '/api/auth/forgot-password', { email });
      setSent(r.via);
    } catch (err) {
      setError(errorKey(err));
    } finally {
      setBusy(false);
    }
  };
  const top = <LanguageSwitch value={ui} onChange={setNativeLocale} />;

  if (mode === 'code') {
    return (
      <AuthFrame title={t('mfaTitle')} top={top}>
        <form onSubmit={submitCode} className="stack">
          <p className="muted">{t('mfaPrompt')}</p>
          <label className="field">
            <span>{t('mfaCode')}</span>
            <input
              inputMode="numeric"
              autoComplete="one-time-code"
              required
              maxLength={20}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              autoFocus
            />
          </label>
          {error && <p className="form-error" role="alert">{t(error)}</p>}
          <button className="btn primary" disabled={busy}>{t('signIn')}</button>
          <p className="muted small">{t('mfaRecoveryHint')}</p>
          <button type="button" className="link-btn" onClick={() => (setMode('login'), setError(null))}>{t('backToSignIn')}</button>
        </form>
      </AuthFrame>
    );
  }

  if (mode === 'forgot') {
    return (
      <AuthFrame title={t('forgotTitle')} top={top}>
        {sent ? (
          <div className="stack">
            <p role="status">{t(sent === 'email' ? 'forgotSentEmail' : 'forgotSentAdmin')}</p>
            <button className="btn" onClick={() => (setMode('login'), setSent(null))}>{t('backToSignIn')}</button>
          </div>
        ) : (
          <form onSubmit={forgot} className="stack">
            <p className="muted">{t('forgotIntro')}</p>
            <label className="field">
              <span>{t('email')}</span>
              <input type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />
            </label>
            {error && <p className="form-error" role="alert">{t(error)}</p>}
            <button className="btn primary" disabled={busy}>{t('forgotSend')}</button>
            <button type="button" className="link-btn" onClick={() => (setMode('login'), setError(null))}>{t('backToSignIn')}</button>
          </form>
        )}
      </AuthFrame>
    );
  }

  // No language picker before sign-in: the page follows the browser language (switch at the top).
  // The native language is chosen once, when the account is created.
  return (
    <AuthFrame title={t('appName')} top={top}>
      <form onSubmit={submit} className="stack">
        <label className="field">
          <span>{t('email')}</span>
          <input type="email" autoComplete="username webauthn" required value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />
        </label>
        <label className="field">
          <span>{t('password')}</span>
          <input type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
        </label>
        {error && <p className="form-error" role="alert">{t(error)}</p>}
        {faceId && (
          <button type="button" className="btn primary faceid-btn" onClick={() => void faceIdLogin()} disabled={busy}>
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 8V6a2 2 0 0 1 2-2h2M16 4h2a2 2 0 0 1 2 2v2M20 16v2a2 2 0 0 1-2 2h-2M8 20H6a2 2 0 0 1-2-2v-2M9 9.5v1M15 9.5v1M12 9.5V13h-1M9.5 16c1.5 1 3.5 1 5 0" />
            </svg>
            {nativeAndroid ? t('bioSignIn') : t('faceIdSignIn')}
          </button>
        )}
        <button className={`btn ${faceId ? '' : 'primary'}`} disabled={busy}>{busy ? t('signingIn') : t('signIn')}</button>
        <button type="button" className="link-btn forgot-link" onClick={() => (setMode('forgot'), setError(null))}>{t('forgotPassword')}</button>
        {passkeysSupported() && (
          <>
            <div className="or-divider"><span>{t('or')}</span></div>
            <button type="button" className="btn passkey-btn" onClick={() => void passkey()} disabled={busy}>
              <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="9" cy="8" r="4" />
                <path d="M2 21c0-3.9 3.1-7 7-7 1.3 0 2.5.3 3.5.9" />
                <circle cx="18" cy="13" r="2.5" />
                <path d="M18 15.5V22l1.5-1.2L18 19.5l1.5-1.2" />
              </svg>
              {t('signInPasskey')}
            </button>
          </>
        )}
        <p className="muted small">{t('inviteOnly')}</p>
      </form>
    </AuthFrame>
  );
}

export function AcceptInvite({ token }: { token: string }) {
  // Order: 1) name, e-mail, password  2) native language (once)  3) privacy consent → account.
  const native = useStore((s) => s.nativeLocale);
  const [step, setStep] = useState<'details' | 'language' | 'confirm'>('details');
  const [uiLocale, setUiLocale] = useState(native ? uiFor(native) : browserUiLocale());
  const t = (k: MsgKey, v?: Record<string, string | number>) => translate(uiLocale, k, v);
  const [info, setInfo] = useState<{ organizationName: string; email: string | null } | null>(null);
  const [invalid, setInvalid] = useState(false);
  const [form, setForm] = useState({ displayName: '', email: '', password: '', readingLocale: native ?? uiLocale });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [privacy, setPrivacy] = useState<PrivacyInfoDTO | null>(null);
  const [consent, setConsent] = useState({ processing: false, crossBorder: false });

  useEffect(() => {
    api<PrivacyInfoDTO>('GET', '/api/privacy').then(setPrivacy).catch(() => {});
  }, []);

  useEffect(() => {
    api<{ organizationName: string; email: string | null }>('GET', `/api/invites/${token}`)
      .then((r) => {
        setInfo(r);
        if (r.email) setForm((f) => ({ ...f, email: r.email! }));
      })
      .catch(() => setInvalid(true));
  }, [token]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (!privacy) return;
      await api('POST', '/api/auth/accept-invite', {
        token,
        ...form,
        uiLocale,
        noticeVersion: privacy.version,
        consentProcessing: consent.processing,
        consentCrossBorder: consent.crossBorder,
      });
      location.replace('/');
    } catch (err) {
      setError(err instanceof ApiError && err.code === 'validation' ? t('passwordHint') : err instanceof ApiError && err.code === 'email_taken' ? err.code : t(errorKey(err)));
      setBusy(false);
    }
  };

  if (step === 'language')
    return (
      <LanguageStep
        onPick={(l) => {
          setNativeLocale(l);
          setUiLocale(uiFor(l));
          setForm((f) => ({ ...f, readingLocale: l }));
          setStep('confirm');
        }}
      />
    );
  if (invalid) return <AuthFrame title={t('appName')}><p role="alert">{t('inviteInvalid')}</p></AuthFrame>;
  if (!info) return <div className="splash" aria-busy="true">…</div>;
  const back = (
    <button type="button" className="link-btn" onClick={() => setStep('details')}>
      ‹ {t('back')}
    </button>
  );
  if (step === 'details')
    return (
      <AuthFrame title={t('joinTitle', { org: info.organizationName })} top={<LanguageSwitch value={uiLocale} onChange={setUiLocale} />}>
        <form
          className="stack"
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            setStep('language');
          }}
        >
          <label className="field">
            <span>{t('displayName')}</span>
            <input required maxLength={80} value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })} autoComplete="name" autoFocus />
          </label>
          <label className="field">
            <span>{t('email')}</span>
            <input type="email" required value={form.email} readOnly={!!info.email} onChange={(e) => setForm({ ...form, email: e.target.value })} autoComplete="username" />
          </label>
          <label className="field">
            <span>{t('password')}</span>
            <input type="password" required minLength={10} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} autoComplete="new-password" />
            <small className="muted">{t('passwordHint')}</small>
          </label>
          {error && <p className="form-error" role="alert">{error}</p>}
          <button className="btn primary">{t('next')}</button>
        </form>
      </AuthFrame>
    );
  return (
    <AuthFrame title={t('joinTitle', { org: info.organizationName })} top={back}>
      <form onSubmit={submit} className="stack">
        <div className="native-lang">
          <span className={`seal seal-${form.readingLocale}`}>{seal(form.readingLocale)}</span>
          <span>
            <span className="muted">{t('nativeLanguage')}: </span>
            <strong lang={form.readingLocale}>{NATIVE_NAMES[form.readingLocale] ?? form.readingLocale}</strong>
          </span>
          <button type="button" className="link-btn" onClick={() => setStep('language')}>{t('change')}</button>
          <small className="muted">{t('nativeLanguageHint')}</small>
        </div>
        <p className="muted small">
          {form.displayName} · {form.email}
        </p>
        {privacy && <ConsentFields info={privacy} uiLocale={uiLocale} value={consent} onChange={setConsent} />}
        {error && (
          <p className="form-error" role="alert">
            {error} {back}
          </p>
        )}
        <button className="btn primary" disabled={busy || !privacy || !consent.processing || !consent.crossBorder}>{t('createAccount')}</button>
      </form>
    </AuthFrame>
  );
}

export function ResetPassword({ token }: { token: string }) {
  const t = useT();
  const [password, setPassword] = useState('');
  const [state, setState] = useState<'idle' | 'busy' | 'done'>('idle');
  const [error, setError] = useState<MsgKey | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setState('busy');
    try {
      await api('POST', '/api/auth/reset-password', { token, password });
      setState('done');
    } catch (err) {
      setError(err instanceof ApiError && err.code === 'validation' ? 'passwordHint' : errorKey(err));
      setState('idle');
    }
  };
  return (
    <AuthFrame title={t('resetTitle')}>
      {state === 'done' ? (
        <div className="stack">
          <p>{t('resetDone')}</p>
          <a className="btn primary" href="/">{t('signIn')}</a>
        </div>
      ) : (
        <form onSubmit={submit} className="stack">
          <label className="field">
            <span>{t('newPassword')}</span>
            <input type="password" minLength={10} required value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" autoFocus />
            <small className="muted">{t('passwordHint')}</small>
          </label>
          {error && <p className="form-error" role="alert">{t(error)}</p>}
          <button className="btn primary" disabled={state === 'busy'}>{t('savePassword')}</button>
        </form>
      )}
    </AuthFrame>
  );
}

/** Shown to signed-in users who have not accepted the current privacy notice (new or changed). */
export function ConsentScreen() {
  const t = useT();
  const me = useStore((s) => s.me);
  const privacy = useStore((s) => s.privacy);
  const ui = me?.uiLocale ?? browserUiLocale();
  const [consent, setConsent] = useState({ processing: false, crossBorder: false });
  const changeUi = async (uiLocale: string) => {
    try {
      setMe(await api<MeDTO>('PATCH', '/api/me', { uiLocale }));
    } catch {
      /* keep current language */
    }
  };
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!privacy) return null;
  return (
    <AuthFrame title={t('consentTitle')} top={<LanguageSwitch value={ui} onChange={(l) => void changeUi(l)} />}>
      <div className="stack">
        <p className="muted">{t('consentIntro')}</p>
        <div className="notice-box">
          <PrivacyNoticeText info={privacy} uiLocale={ui} />
        </div>
        <ConsentFields info={privacy} uiLocale={ui} value={consent} onChange={setConsent} showNoticeToggle={false} />
        {error && <p className="form-error" role="alert">{error}</p>}
        <button
          className="btn primary"
          disabled={busy || !consent.processing || !consent.crossBorder}
          onClick={async () => {
            setBusy(true);
            setError(null);
            try {
              await acceptNotice(privacy.version);
            } catch (err) {
              setError(t('error_generic', { code: err instanceof ApiError ? err.code : 'network' }));
            } finally {
              setBusy(false);
            }
          }}
        >
          {t('acceptAndContinue')}
        </button>
        <button className="btn ghost" onClick={() => void logout()}>{t('declineSignOut')}</button>
      </div>
    </AuthFrame>
  );
}
